import React, { useState } from "react";
import {
  Box,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useTranslation } from "react-i18next";

import {
  extractGradientStops,
  isGradient,
  isHexColor,
  isRgbaTuple,
  normalizeHexTo6,
  replaceGradientStop,
  toCssColor,
} from "../constants";

interface ColorsPanelProps {
  colors: Record<string, string>;
  originalColors: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onDelete: (key: string) => void;
}

const pickerStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  border: "none",
  background: "none",
  cursor: "pointer",
  padding: 0,
  flexShrink: 0,
};

const ColorField: React.FC<{
  name: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ name, value, onChange }) => {
  const hex = isHexColor(value);
  const gradient = !hex && isGradient(value);
  const rgbaTuple = !hex && !gradient && isRgbaTuple(value);
  const stops = gradient ? extractGradientStops(value) : [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography
        variant="caption"
        color="text.primary"
        sx={{ overflowWrap: "anywhere", lineHeight: 1.3 }}
      >
        {name}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {hex && (
          <input
            type="color"
            value={normalizeHexTo6(value)}
            onChange={(e) => onChange(e.target.value)}
            style={pickerStyle}
            aria-label={name}
          />
        )}
        {gradient && (
          <>
            <Box
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                borderRadius: "4px",
                border: "1px solid",
                borderColor: "divider",
                backgroundImage: value,
              }}
            />
            {stops.map((stop, i) => (
              <input
                key={i}
                type="color"
                value={stop.hex6}
                onChange={(e) =>
                  onChange(replaceGradientStop(value, i, e.target.value))
                }
                style={pickerStyle}
                aria-label={`${name} stop ${i + 1}`}
              />
            ))}
          </>
        )}
        {rgbaTuple && (
          <Box
            sx={{
              width: 28,
              height: 28,
              flexShrink: 0,
              borderRadius: "4px",
              border: "1px solid",
              borderColor: "divider",
            }}
            style={{ backgroundColor: toCssColor(value) }}
          />
        )}
        <TextField
          size="small"
          fullWidth
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{ "aria-label": name }}
        />
      </Box>
    </Box>
  );
};

const ColorsPanel: React.FC<ColorsPanelProps> = ({
  colors,
  originalColors,
  onChange,
  onDelete,
}) => {
  const { t } = useTranslation(["corporate-brand-asset"]);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("#000000");

  const keys = Object.keys(colors);
  const isDuplicate = newKey.trim() !== "" && keys.includes(newKey.trim());

  const sortGradientKeys = (gradientKeys: string[]): string[] =>
    [...gradientKeys].sort((a, b) => {
      const base = (k: string) => k.replace(/(Start|End)$/i, "");
      if (base(a) !== base(b)) return base(a).localeCompare(base(b));
      return /Start$/i.test(a) ? -1 : 1;
    });
  const belongsToGradients = (key: string) =>
    /^gradient/i.test(key) || isGradient(colors[key]);
  const gradientKeys = sortGradientKeys(keys.filter(belongsToGradients));
  const solidKeys = keys
    .filter((key) => !belongsToGradients(key))
    .sort((a, b) => a.localeCompare(b));

  const handleAdd = () => {
    const k = newKey.trim();
    if (!k || isDuplicate) return;
    onChange(k, newValue);
    setNewKey("");
    setNewValue("#000000");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {keys.length === 0 && (
        <Typography variant="body2" color="text.primary">
          {t("colors.empty")}
        </Typography>
      )}

      {[
        { label: t("colors.groupSolid"), groupKeys: solidKeys },
        { label: t("colors.groupGradient"), groupKeys: gradientKeys },
      ]
        .filter((group) => group.groupKeys.length > 0)
        .map((group) => (
          <Box key={group.label}>
            <Typography
              variant="overline"
              color="text.primary"
              sx={{ display: "block", mb: 1 }}
            >
              {group.label}
            </Typography>
            <Grid container spacing={2}>
              {group.groupKeys.map((key) => {
                const originalValue = originalColors[key];
                const canReset =
                  originalValue !== undefined && originalValue !== colors[key];

                return (
                  <Grid item xs={12} md={6} key={key}>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-end", gap: 0.5 }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <ColorField
                          name={key}
                          value={colors[key]}
                          onChange={(value) => onChange(key, value)}
                        />
                      </Box>
                      <Tooltip title={t("colors.reset")}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={!canReset}
                            onClick={() => onChange(key, originalValue)}
                            aria-label={t("colors.reset")}
                          >
                            <RestartAltIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t("colors.delete")}>
                        <IconButton
                          size="small"
                          onClick={() => onDelete(key)}
                          aria-label={t("colors.delete")}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}

      <Divider />
      <Typography variant="subtitle2" color="text.primary">
        {t("colors.addTitle")}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <input
          type="color"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          style={{
            width: 36,
            height: 36,
            border: "none",
            background: "none",
            cursor: "pointer",
            padding: 0,
            flexShrink: 0,
            marginTop: 4,
          }}
        />
        <TextField
          size="small"
          label={t("colors.addName")}
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          error={isDuplicate}
          helperText={isDuplicate ? t("colors.duplicate") : undefined}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          label={t("colors.addValue")}
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          sx={{ flex: 1 }}
        />
        <IconButton
          onClick={handleAdd}
          disabled={!newKey.trim() || isDuplicate}
          color="primary"
          sx={{ mt: "2px" }}
        >
          <AddIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ColorsPanel;
